import React from 'react';
import { Paper, Grid, Typography, styled } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { IContato } from '../../../commons/types/empresa/contatos';
import Logo from '../../../assets/STATO-INTOO-LOGO-MY-INTOO-VERS-2 A.png';
import { GridItem } from '../../../commons/styleds/styleds';
import { useTranslation } from '../../../commons/useTranslasion';
import { useDateFormatters } from '../../../utils/useDateFormatters';

interface AssessoradosPDFProps {
  contatos: IContato[];
  statusCount: Record<string, number>;
  columns: GridColDef[];
}

const AssessoradosPDF: React.FC<AssessoradosPDFProps> = ({
  contatos,
  statusCount,
  columns
}) => {
  const { translate } = useTranslation();
  const { formatarData } = useDateFormatters();
  return (
    <GlobalStyle>
      <PDFContainer>
        <PDFContent>
          <PDFHeader>
            <HeaderContent>
              <LogoContainer>
                <img src={Logo} alt="logo" height="40px" />
              </LogoContainer>
              <TitleContainer>
                <Typography
                  variant="h5"
                  sx={{
                    mt: 1,
                    color: '#1976d2',
                    fontWeight: 'bold',
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: '1.1rem',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -4,
                      left: 0,
                      width: '100%',
                      height: '2px',
                      backgroundColor: '#1976d2'
                    }
                  }}
                >
                  {translate("relatorioDeAcompanhamentoDosProcessosDeTransmissaoDeCarreira")}
                </Typography>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  {translate("geradoEm")} {formatarData(new Date())}
                </Typography>
              </TitleContainer>
            </HeaderContent>
          </PDFHeader>

          <PDFSection>
            <Grid container spacing={2}>
              {Object.entries(statusCount || {}).map(([status, count]) => (
                <Grid item xs={3} key={status}>
                  <StatusCard elevation={0}>
                    <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      {count}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {status}
                    </Typography>
                  </StatusCard>
                </Grid>
              ))}
            </Grid>
          </PDFSection>

          <PDFSection>
            <div style={{ height: 'auto', width: '100%', minHeight: 400 }}>
              <DataGrid
                getRowId={(row) => row.id + row.nome}
                rows={contatos}
                columns={columns}
                disableColumnMenu
                hideFooter
                autoHeight
                density="compact"
                sx={{
                  border: 'none',
                  backgroundColor: 'white',
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #e0e0e0',
                    color: '#333'
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #1976d2'
                  }
                }}
              />
            </div>
          </PDFSection>

          <PDFFooter>
            <Typography variant="body2" sx={{ color: '#666' }}>
              © {new Date().getFullYear()} STATO - Todos os direitos reservados
            </Typography>
          </PDFFooter>
        </PDFContent>
      </PDFContainer>
    </GlobalStyle>
  );
};

export const LogoGridItem = styled(GridItem)`
  background: transparent;
`;

const GlobalStyle = styled('div')`
  --green2: #3cb99c;
  --green3: #5cc2aa;
  --blue1: #0288d1;
  --blue2: #1976d2;
  --blue3: #2196f3;
`;

const PDFContainer = styled('div')`
  background: white;
  width: 100%;
  height: 100%;
  min-height: 210mm;
  display: flex;
  flex-direction: column;
  padding: 20mm; // Standard print margins
`;

const PDFContent = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PDFHeader = styled('div')`
  width: 100%;
  padding: 20px;
  margin-bottom: 20px;
  background: linear-gradient(
    90deg,
    var(--green2) 0%,
    var(--green2) 20%,
    var(--green3) 40%,
    var(--green3) 60%,
    var(--blue3) 75%,
    var(--blue2) 100%
  );
  transition: width 195ms cubic-bezier(0.4, 0, 0.6, 1), margin 195ms cubic-bezier(0.4, 0, 0.6, 1);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: var(--blue2);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const HeaderContent = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const LogoContainer = styled('div')`
  flex: 0 0 auto;
`;

const TitleContainer = styled('div')`
  flex: 1;
  
  .MuiTypography-root {
    color: white !important;
  }
`;
const PDFSection = styled('div')`
  width: 100%;
  margin: 10mm 0;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }

  // Adjust DataGrid container
  .MuiDataGrid-root {
    width: 100%;
    height: auto !important;
    min-height: 100mm !important;
    
    .MuiDataGrid-main {
      width: 100%;
    }
    
    .MuiDataGrid-virtualScroller {
      overflow: visible !important;
    }
  }
`;

const StatusCard = styled(Paper)`
  padding: 15px;
  text-align: center;
  background-color: #f8f9fa !important;
  border: 1px solid #e0e0e0;
`;

const PDFFooter = styled('div')`
  margin-top: 40px;
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`;

export default AssessoradosPDF;